import { PAIRING_API_URL, STORAGE } from '../constants'

export interface PairingValues {
  serverUrl: string
  torrentioUrl: string
}

export interface PairingSession {
  code: string
  pollToken: string
  expiresAt: number
}

export interface TvRemoteCredentials {
  deviceId: string
  tvToken: string
  deviceName: string
}

export interface ControllerRemoteCredentials {
  deviceId: string
  controllerId: string
  controllerToken: string
  deviceName: string
}

export interface TvPairingResult {
  values: PairingValues
  remote: TvRemoteCredentials
}

export interface ControllerProfile extends ControllerRemoteCredentials, PairingValues {
  pairedAt: number
  /** Phone-local alias so several LG sets are distinguishable without storing
   * the name in the relay. */
  localName?: string
}

export interface RemoteDeviceStatus {
  deviceName: string
  tvOnline: boolean
  controllers: number
  connectedControllers: number
  role: 'tv' | 'controller'
}

export const CONTROLLER_PROFILE_LIMIT = 12

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function validRemoteToken(value: unknown): value is string {
  return typeof value === 'string' && value.length >= 32 && value.length <= 128
    && /^[A-Za-z0-9_-]+$/.test(value)
}

function validHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 500) return false
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch { return false }
}

function cleanTvRemote(value: unknown): TvRemoteCredentials | null {
  if (!record(value) || typeof value.deviceId !== 'string' || !/^[a-f0-9]{32}$/.test(value.deviceId)
    || !validRemoteToken(value.tvToken)) return null
  return {
    deviceId: value.deviceId,
    tvToken: value.tvToken,
    deviceName: typeof value.deviceName === 'string' && value.deviceName.trim()
      ? value.deviceName.trim().slice(0, 80) : 'Vela TV',
  }
}

function cleanPairingValues(value: unknown): PairingValues | null {
  if (!record(value) || !validHttpUrl(value.serverUrl) || !validHttpUrl(value.torrentioUrl)) return null
  return { serverUrl: value.serverUrl, torrentioUrl: value.torrentioUrl }
}

function cleanControllerCredentials(value: unknown): ControllerRemoteCredentials | null {
  if (!record(value) || typeof value.deviceId !== 'string' || !/^[a-f0-9]{32}$/.test(value.deviceId)
    || typeof value.controllerId !== 'string' || !/^[A-Za-z0-9_-]{8,128}$/.test(value.controllerId)
    || !validRemoteToken(value.controllerToken)) return null
  return {
    deviceId: value.deviceId,
    controllerId: value.controllerId,
    controllerToken: value.controllerToken,
    deviceName: typeof value.deviceName === 'string' && value.deviceName.trim()
      ? value.deviceName.trim().slice(0, 80) : 'Vela TV',
  }
}

function cleanControllerProfile(value: unknown): ControllerProfile | null {
  if (!record(value) || typeof value.deviceId !== 'string' || !/^[a-f0-9]{32}$/.test(value.deviceId)
    || typeof value.controllerId !== 'string' || !/^[A-Za-z0-9_-]{8,128}$/.test(value.controllerId)
    || !validRemoteToken(value.controllerToken)
    || !validHttpUrl(value.serverUrl) || !validHttpUrl(value.torrentioUrl)
    || typeof value.pairedAt !== 'number' || !Number.isFinite(value.pairedAt) || value.pairedAt <= 0) return null
  return {
    deviceId: value.deviceId,
    controllerId: value.controllerId,
    controllerToken: value.controllerToken,
    deviceName: typeof value.deviceName === 'string' && value.deviceName.trim()
      ? value.deviceName.trim().slice(0, 80) : 'Vela TV',
    serverUrl: value.serverUrl,
    torrentioUrl: value.torrentioUrl,
    pairedAt: Math.min(value.pairedAt, 9_999_999_999_999),
    localName: typeof value.localName === 'string' && value.localName.trim()
      ? value.localName.trim().slice(0, 80) : undefined,
  }
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch {
    return fallback
  }
}

function write<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value))
}

/** Short by design: this API answers in milliseconds or it is not there. */
const PAIRING_TIMEOUT = 10_000

async function pairingRequest(path: string, init?: RequestInit) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined
  const timer = setTimeout(() => controller?.abort(), PAIRING_TIMEOUT)
  let response: Response
  try {
    response = await fetch(`${PAIRING_API_URL}${path}`, { ...init, signal: controller?.signal })
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw new Error('The pairing service did not respond.')
    throw error
  } finally {
    clearTimeout(timer)
  }
  if (!response.ok && response.status !== 204) {
    let message = `Pairing service failed (${response.status})`
    try {
      const body = await response.json() as { error?: string }
      if (body.error) message = body.error
    } catch { /* the status is still useful */ }
    throw new Error(message)
  }
  return response
}

function jsonBody(value: unknown): RequestInit {
  return {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(value),
  }
}

export function getTvRemoteCredentials() {
  return cleanTvRemote(read<unknown>(STORAGE.tvRemote, null))
}

export function saveTvRemoteCredentials(remote: TvRemoteCredentials) {
  const clean = cleanTvRemote(remote)
  if (!clean) throw new Error('The TV pairing service returned invalid credentials.')
  write(STORAGE.tvRemote, clean)
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('vela:remote-credentials'))
  return clean
}

export function clearTvRemoteCredentials() {
  localStorage.removeItem(STORAGE.tvRemote)
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('vela:remote-credentials'))
}

export function getControllerProfiles() {
  const value = read<unknown>(STORAGE.controllerRemotes, [])
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  return value
    .map(cleanControllerProfile)
    .filter((profile): profile is ControllerProfile => {
      if (!profile) return false
      const key = `${profile.deviceId}:${profile.controllerId}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((a, b) => b.pairedAt - a.pairedAt)
    .slice(0, CONTROLLER_PROFILE_LIMIT)
}

export function saveControllerProfile(credentials: ControllerRemoteCredentials, values: PairingValues) {
  const profile = cleanControllerProfile({
    deviceId: credentials.deviceId,
    controllerId: credentials.controllerId,
    controllerToken: credentials.controllerToken,
    deviceName: credentials.deviceName,
    serverUrl: values.serverUrl,
    torrentioUrl: values.torrentioUrl,
    pairedAt: Date.now(),
  })
  if (!profile) throw new Error('The pairing service returned an invalid controller profile.')
  const current = getControllerProfiles()
  const exists = current.some((value) => value.deviceId === profile.deviceId && value.controllerId === profile.controllerId)
  if (!exists && current.length >= CONTROLLER_PROFILE_LIMIT) {
    throw new Error(`This phone supports up to ${CONTROLLER_PROFILE_LIMIT} paired TVs. Remove one first.`)
  }
  const next = [profile, ...current.filter((value) => (
    value.deviceId !== profile.deviceId || value.controllerId !== profile.controllerId
  ))].slice(0, CONTROLLER_PROFILE_LIMIT)
  write(STORAGE.controllerRemotes, next)
  return profile
}

export function updateControllerProfile(profile: ControllerProfile) {
  const clean = cleanControllerProfile(profile)
  if (!clean) throw new Error('This saved controller profile is invalid.')
  const next = [clean, ...getControllerProfiles().filter((value) => (
    value.deviceId !== clean.deviceId || value.controllerId !== clean.controllerId
  ))]
  write(STORAGE.controllerRemotes, next)
  return clean
}

export function removeControllerProfile(profile: ControllerProfile) {
  const next = getControllerProfiles().filter((value) => (
    value.deviceId !== profile.deviceId || value.controllerId !== profile.controllerId
  ))
  write(STORAGE.controllerRemotes, next)
  return next
}

export async function createPairingSession(values: PairingValues): Promise<PairingSession> {
  const existing = getTvRemoteCredentials()
  const response = await pairingRequest('/sessions', {
    method: 'POST',
    ...jsonBody(existing ? { remote: existing, values } : {}),
  })
  const value = await response.json() as unknown
  if (!record(value) || typeof value.code !== 'string' || !/^[23456789A-HJ-NP-Z]{8}$/.test(value.code)
    || !validRemoteToken(value.pollToken) || typeof value.expiresAt !== 'number' || !Number.isFinite(value.expiresAt)
    || value.expiresAt <= Date.now() || value.expiresAt > Date.now() + 11 * 60_000) {
    throw new Error('Pairing service returned an invalid session.')
  }
  return { code: value.code, pollToken: value.pollToken, expiresAt: value.expiresAt }
}

export async function pollPairingSession(session: PairingSession): Promise<TvPairingResult | null> {
  const response = await pairingRequest(`/sessions/${encodeURIComponent(session.code)}`, {
    headers: { Authorization: `Bearer ${session.pollToken}` },
  })
  if (response.status === 204) return null
  const value = await response.json() as unknown
  if (!record(value)) throw new Error('Pairing service returned an invalid result.')
  const values = cleanPairingValues(value.values)
  const remote = cleanTvRemote(value.remote)
  if (!values || !remote) throw new Error('Pairing service returned an invalid result.')
  return { values, remote }
}

export async function finishPairingSession(session: PairingSession) {
  await pairingRequest(`/sessions/${encodeURIComponent(session.code)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${session.pollToken}` },
  })
}

export async function submitPairingValues(code: string, values: PairingValues, controllerName = 'Phone') {
  const response = await pairingRequest(`/sessions/${encodeURIComponent(code.trim().toUpperCase())}`, {
    method: 'PUT',
    ...jsonBody({ ...values, controllerName }),
  })
  const value = await response.json() as unknown
  const credentials = cleanControllerCredentials(value)
  const cleanValues = cleanPairingValues(value)
  if (!credentials || !cleanValues) throw new Error('Pairing service returned invalid controller credentials.')
  return { ...credentials, ...cleanValues }
}

export async function getRemoteStatus(deviceId: string, token: string): Promise<RemoteDeviceStatus> {
  const response = await pairingRequest(`/devices/${encodeURIComponent(deviceId)}/status`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const value = await response.json() as unknown
  if (!record(value) || typeof value.deviceName !== 'string' || value.deviceName.length > 80
    || typeof value.tvOnline !== 'boolean' || !Number.isInteger(value.controllers) || !Number.isInteger(value.connectedControllers)
    || typeof value.controllers !== 'number' || value.controllers < 0 || value.controllers > 12
    || typeof value.connectedControllers !== 'number' || value.connectedControllers < 0 || value.connectedControllers > 12
    || (value.role !== 'tv' && value.role !== 'controller')) throw new Error('Pairing service returned an invalid device status.')
  return {
    deviceName: value.deviceName,
    tvOnline: value.tvOnline,
    controllers: value.controllers,
    connectedControllers: value.connectedControllers,
    role: value.role,
  }
}

export async function unlinkTvRemote(remote = getTvRemoteCredentials()) {
  if (!remote) return
  await pairingRequest(`/devices/${encodeURIComponent(remote.deviceId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${remote.tvToken}` },
  })
  clearTvRemoteCredentials()
}

export async function unlinkController(profile: ControllerProfile) {
  await pairingRequest(`/devices/${encodeURIComponent(profile.deviceId)}/controllers/current`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${profile.controllerToken}` },
  })
  removeControllerProfile(profile)
}

export async function rotateTvToken(remote = getTvRemoteCredentials()) {
  if (!remote) throw new Error('This TV is not paired.')
  const response = await pairingRequest(`/devices/${encodeURIComponent(remote.deviceId)}/rotate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${remote.tvToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ role: 'tv' }),
  })
  const result = await response.json() as unknown
  if (!record(result) || !validRemoteToken(result.tvToken)) throw new Error('Pairing service returned an invalid TV key.')
  return saveTvRemoteCredentials({ ...remote, tvToken: result.tvToken })
}

export async function rotateControllerToken(profile: ControllerProfile) {
  const response = await pairingRequest(`/devices/${encodeURIComponent(profile.deviceId)}/rotate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${profile.controllerToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ role: 'controller' }),
  })
  const result = await response.json() as unknown
  if (!record(result) || !validRemoteToken(result.controllerToken)) throw new Error('Pairing service returned an invalid controller key.')
  return updateControllerProfile({ ...profile, controllerToken: result.controllerToken })
}

export function remoteWebSocketUrl(deviceId: string) {
  const url = new URL(`${PAIRING_API_URL}/devices/${encodeURIComponent(deviceId)}/connect`)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}
